import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Typography,
  Divider,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Avatar,
  IconButton,
  InputAdornment,
  Checkbox
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { contractorsApi, workOrdersApi, classificationApi } from '../../services/api';

const IssueWorkOrderModal = ({ 
  open, 
  onClose, 
  defectData, 
  onWorkOrderCreated 
}) => {
  const { user, getUserOrganization } = useAuth();
  const [assignmentType, setAssignmentType] = useState('internal');
  const [selectedUsers, setSelectedUsers] = useState([]); // Changed to handle multiple users
  const [selectedContractor, setSelectedContractor] = useState('');
  const [matchingContractors, setMatchingContractors] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [userFilter, setUserFilter] = useState('network_section'); // Default to network/section users
  const [allInternalUsers, setAllInternalUsers] = useState([]); // Store all users for filtering
  const [selectOpen, setSelectOpen] = useState(false); // Control Select dropdown state
  const [userSearchTerm, setUserSearchTerm] = useState(''); // Search term for filtering users
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [slaData, setSlaData] = useState(null);
  const [dueDateManuallyChanged, setDueDateManuallyChanged] = useState(false);

  const [workOrderData, setWorkOrderData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    dueDate: null
  });

  useEffect(() => {
    if (open && defectData) {
      // Reset all state completely
      setError('');
      setInternalUsers([]);
      setAllInternalUsers([]);
      setMatchingContractors([]);
      setSelectedUsers([]);
      setSelectedContractor('');
      setDueDateManuallyChanged(false);
      setSlaData(null); // Reset SLA data on open
      setSelectOpen(false); // Reset select state
      
      // Reset filter to default BEFORE fetching data
      setUserFilter('network_section');
      setUserSearchTerm(''); // Reset search term
      
      // Debug: Log the defect data structure
      console.log('Defect Data Structure:', {
        defectData,
        rawData: defectData?.rawData,
        keys: Object.keys(defectData || {}),
        rawDataKeys: defectData?.rawData ? Object.keys(defectData.rawData) : 'No rawData'
      });
      
      initializeWorkOrder(null); // Initialize without SLA data first
      fetchAssignmentOptions();
      fetchSlaData();
    }
  }, [open, defectData]);

  useEffect(() => {
    if (open && defectData) {
      initializeWorkOrder(slaData);
    }
  }, [slaData]);

  // Apply filter whenever userFilter, userSearchTerm, or allInternalUsers changes
  useEffect(() => {
    if (allInternalUsers.length > 0) {
      applyUserFilter(allInternalUsers, userFilter, userSearchTerm);
    }
  }, [userFilter, userSearchTerm, allInternalUsers]);

  const initializeWorkOrder = (currentSlaData) => {
    const title = `Repair ${defectData.defect_type_name || 'Defect'} - ${defectData.asset_name || 'Asset'}`;
    
    setWorkOrderData({
      title,
      description: `Repair required for ${defectData.defect_type_name || 'defect'} detected on ${defectData.asset_name || 'asset'}.${defectData.notes ? '\n\nDefect Notes: ' + defectData.notes : ''}`,
      priority: getPriorityFromSeverity(defectData.severity_level),
      dueDate: new Date(generateDefaultDueDate(defectData.severity_level, currentSlaData))
    });
  };

  const getPriorityFromSeverity = (severityLevel) => {
    if (!severityLevel) return 'normal';
    const level = severityLevel.toLowerCase();
    if (level.includes('critical') || level.includes('high')) return 'high';
    if (level.includes('medium') || level.includes('moderate')) return 'normal';
    return 'low';
  };

  const generateDefaultDueDate = (severityLevel, sla) => {
    if (sla && (sla.permanent_fix_sla_hours || sla.temp_fix_sla_hours)) {
      const slaHours = sla.permanent_fix_sla_hours || sla.temp_fix_sla_hours;
      const createdAt = new Date(defectData.rawData.created_at || defectData.rawData.first_detected_at);
      let dueDate = new Date(createdAt.getTime() + slaHours * 60 * 60 * 1000);

      if (dueDate < new Date()) {
        // SLA is already overdue, set due date to 24 hours from now
        dueDate = new Date();
        dueDate.setHours(dueDate.getHours() + 24);
      }
      return dueDate.toISOString();
    }

    const today = new Date();
    let daysToAdd = 7;

    if (severityLevel) {
      const level = severityLevel.toLowerCase();
      if (level.includes('critical')) daysToAdd = 1;
      else if (level.includes('high')) daysToAdd = 3;
      else if (level.includes('medium')) daysToAdd = 7;
      else daysToAdd = 14;
    }

    const dueDate = new Date(today);
    dueDate.setDate(today.getDate() + daysToAdd);
    // Set a default time, e.g., 5:00 PM
    dueDate.setHours(17, 0, 0, 0);
    return dueDate.toISOString();
  };

  const fetchSlaData = async () => {
    if (!defectData?.rawData?.defect_type_id) return;

    try {
      const response = await classificationApi.getDefectTypeById(defectData.rawData.defect_type_id);
      setSlaData(response.data);
    } catch (error) {
      console.error('Failed to fetch SLA data:', error);
      // Do not show an error to the user, as the modal can still function without SLA data
    }
  };

  const fetchAssignmentOptions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [contractorsResponse, usersResponse] = await Promise.all([
        fetchMatchingContractors(),
        fetchInternalUsers()
      ]);
      
      console.log('Assignment options loaded:', {
        contractors: contractorsResponse?.length || 0,
        users: usersResponse?.length || 0
      });
      
    } catch (error) {
      console.error('Failed to fetch assignment options:', error);
      setError('Failed to load assignment options. Please try again.');
      setMatchingContractors([]);
      setInternalUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchingContractors = async () => {
    try {
      console.log('Fetching matching contractors...');
      const organizationInfo = getUserOrganization();
      
      if (!organizationInfo?.id) {
        throw new Error('Organization information not available');
      }
      
      const rawDefectData = defectData.rawData || defectData;
      const defectTypeId = rawDefectData.defect_type_id || rawDefectData.validated_defect_type_id;
      const sectionId = rawDefectData.section_id || rawDefectData.matched_section_id;
      const networkId = rawDefectData.network_id;
      
      if (!defectTypeId || !sectionId) {
        throw new Error('Required defect data not available');
      }
      
      const response = await contractorsApi.findMatches({
        defectTypeId: defectTypeId,
        networkId: networkId,
        sectionId: sectionId,
        organizationId: organizationInfo.id
      });
      
      const contractors = Array.isArray(response.data) ? response.data : 
                         (response.data?.data && Array.isArray(response.data.data)) ? response.data.data : 
                         [];
      
      setMatchingContractors(contractors);
      return contractors;
      
    } catch (error) {
      console.error('Failed to fetch matching contractors:', error);
      setMatchingContractors([]);
      throw error;
    }
  };

  const fetchInternalUsers = async () => {
    try {
      const rawDefectData = defectData.rawData || defectData;
      const sectionId = rawDefectData.section_id || rawDefectData.matched_section_id;
      const networkId = rawDefectData.network_id;

      const response = await contractorsApi.getInternalUsers({
        sectionId,
        networkId,
      });
      const users = Array.isArray(response.data) ? response.data :
                   (response.data?.data && Array.isArray(response.data.data)) ? response.data.data :
                   [];
      
      setAllInternalUsers(users);
      return users;
      
    } catch (error) {
      console.error('Failed to fetch internal users:', error);
      setAllInternalUsers([]);
      setInternalUsers([]);
      throw error;
    }
  };

  // Filter users based on permission scope and search term
  const applyUserFilter = (users, filter, searchTerm = '') => {
    let filteredUsers = [];
    
    // First filter by permission scope
    switch (filter) {
      case 'organization':
        filteredUsers = users.filter(user => user.permission_scope === 'organization');
        break;
      case 'network_section':
        filteredUsers = users.filter(user => user.permission_scope === 'network_section');
        break;
      case 'all':
      default:
        filteredUsers = users;
        break;
    }
    
    // Then filter by search term if provided
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filteredUsers = filteredUsers.filter(user => {
        const fullName = (user.full_name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const role = (user.role || '').toLowerCase();
        
        return fullName.includes(searchLower) || 
               email.includes(searchLower) || 
               role.includes(searchLower);
      });
    }
    
    console.log(`🔍 [User Filter] Applied filter "${filter}" with search "${searchTerm}":`, {
      totalUsers: users.length,
      scopeFilteredUsers: filter === 'all' ? users.length : users.filter(u => 
        filter === 'organization' ? u.permission_scope === 'organization' : u.permission_scope === 'network_section'
      ).length,
      finalFilteredUsers: filteredUsers.length,
      searchTerm: searchTerm
    });
    
    setInternalUsers(filteredUsers);
  };

  // Handle filter changes
  const handleUserFilterChange = (newFilter, event) => {
    // Prevent the Select dropdown from closing
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    setUserFilter(newFilter);
    applyUserFilter(allInternalUsers, newFilter, userSearchTerm);
    // Reset selected user if current selection is not in filtered list
    if (selectedUsers.length > 0) {
      const newSelectedUsers = selectedUsers.filter(userId => {
          return internalUsers.some(user => user.id === userId);
      });
      if (newSelectedUsers.length !== selectedUsers.length) {
          setSelectedUsers(newSelectedUsers);
      }
    }
    
    // Keep the dropdown open
    setSelectOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWorkOrderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (newValue) => {
    setWorkOrderData(prev => ({
      ...prev,
      dueDate: newValue
    }));
    setDueDateManuallyChanged(true);
  };

  const handleAssignmentTypeChange = (e) => {
    setAssignmentType(e.target.value);
    setSelectedUsers([]);
    setSelectedContractor('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!workOrderData.title.trim()) {
      setError('Action log title is required');
      return;
    }

          if (assignmentType === 'internal' && selectedUsers.length === 0) {
        setError('Please select at least one internal user to assign the action log');
        return;
      }

      if (assignmentType === 'external' && !selectedContractor) {
        setError('Please select a contractor company to assign the action log');
        return;
      }

    try {
      setSubmitting(true);
      setError('');

      const workOrder = {
        envis_defect_id: defectData.id,
        title: workOrderData.title,
        description: workOrderData.description,
        priority: workOrderData.priority,
        assignment_type: assignmentType,
        due_date: workOrderData.dueDate ? new Date(workOrderData.dueDate).toISOString() : null
      };

      if (assignmentType === 'internal') {
        workOrder.assigned_to_user_ids = selectedUsers.map(id => parseInt(id));
      } else {
        workOrder.assigned_to_company_id = parseInt(selectedContractor);
      }

      await workOrdersApi.createWorkOrder(workOrder);
      
      setWorkOrderData({
        title: '',
        description: '',
        priority: 'normal',
        dueDate: null
      });
      setSelectedUsers([]);
      setSelectedContractor('');
      setAssignmentType('internal');
      
      onWorkOrderCreated();
      onClose();
    } catch (error) {
      console.error('Failed to create action log:', error);
      
      // Handle specific error types
      if (error.response?.status === 409) {
        // Duplicate action log error
        const errorData = error.response.data;
        const existingWO = errorData.existingWorkOrder;
        setError(
          `Action log already exists for this defect! ` +
          `Existing action log #${existingWO?.id} is ${existingWO?.status}. ` +
          `Only one action log per defect is allowed.`
        );
      } else if (error.response?.status === 403) {
        // Access denied error  
        const errorData = error.response.data;
        setError(errorData.reason || errorData.error || 'Access denied to create action log for this defect.');
      } else {
        // Generic error
        setError(error.response?.data?.message || error.response?.data?.error || 'Failed to create action log. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setError('');
      onClose();
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'default';
      case 'normal': return 'primary';
      case 'high': return 'warning';
      case 'critical': return 'error';
      default: return 'primary';
    }
  };

  const selectedContractorData = selectedContractor && Array.isArray(matchingContractors) 
    ? matchingContractors.find(c => (c.id || c.company_id) === parseInt(selectedContractor))
    : null;

  // Get the actual defect data structure
  const rawDefectData = defectData?.rawData || defectData || {};
  const displayData = {
    asset: rawDefectData.asset_name || 
           rawDefectData.asset_type_name || 
           defectData?.asset_name || 
           defectData?.asset_type_name ||
           'Road Infrastructure',
    defectType: rawDefectData.defect_type_name || 
                defectData?.defect_type_name ||
                rawDefectData.defect_type ||
                defectData?.defect_type ||
                'Unknown Defect',
    location: defectData?.location || // Use the formatted location from DefectsPage
              rawDefectData.section_name || 
              rawDefectData.matched_section_name || 
              defectData?.section_name ||
              rawDefectData.location_name ||
              defectData?.location_name ||
              'Unknown Location',
    severity: rawDefectData.severity_level || 
              defectData?.severity_level ||
              rawDefectData.severity ||
              defectData?.severity ||
              'Not Set',
    network: rawDefectData.network_name || 
             defectData?.network_name ||
             rawDefectData.network ||
             defectData?.network ||
             null,
    defectId: rawDefectData.id || defectData?.id || 'Unknown ID'
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '95vh',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          m: 1
        }
      }}
    >
      {/* Sticky Header */}
      <DialogTitle 
        sx={{ 
          p: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <AssignmentIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
                  Issue Action Log
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create repair assignment for defect #{displayData.defectId}
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={handleClose} disabled={submitting}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Defect Summary Card */}
          <Paper 
            elevation={0}
            sx={{ 
              mt: 2,
              p: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 2
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Asset
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {displayData.asset}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BuildIcon fontSize="small" />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Defect Type
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {displayData.defectType}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon fontSize="small" />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Location
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {displayData.location}
                      {displayData.network && displayData.network !== displayData.location && (
                        <Typography variant="caption" display="block" sx={{ opacity: 0.7 }}>
                          {displayData.network}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Severity
                    </Typography>
                    <Chip 
                      label={displayData.severity} 
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'inherit',
                        fontWeight: 500
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent 
        sx={{ 
          flex: 1,
          p: 0,
          overflow: 'auto'
        }}
      >
        <Box sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {/* Action Log Details Section */}
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <CalendarIcon color="primary" />
                  Action Log Details
                </Typography>
                
                <Stack spacing={3}>
                  <TextField
                    name="title"
                    label="Action Log Title"
                    value={workOrderData.title}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    variant="outlined"
                  />

                  <TextField
                    name="description"
                    label="Description"
                    value={workOrderData.description}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Detailed description of the work to be performed"
                  />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>Priority</InputLabel>
                        <Select
                          name="priority"
                          value={workOrderData.priority}
                          onChange={handleInputChange}
                          label="Priority"
                        >
                          <MenuItem value="low">
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Chip label="Low" size="small" color="default" />
                              <Typography>Low Priority</Typography>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="normal">
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Chip label="Normal" size="small" color="primary" />
                              <Typography>Normal Priority</Typography>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="high">
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Chip label="High" size="small" color="warning" />
                              <Typography>High Priority</Typography>
                            </Stack>
                          </MenuItem>
                          <MenuItem value="critical">
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Chip label="Critical" size="small" color="error" />
                              <Typography>Critical Priority</Typography>
                            </Stack>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DateTimePicker
                        label="Due Date & Time"
                        value={workOrderData.dueDate}
                        onChange={handleDateChange}
                        format="dd/MM/yyyy hh:mm a"
                        renderInput={(params) => <TextField {...params} fullWidth variant="outlined" />}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {(() => {
                        if (dueDateManuallyChanged) {
                          return (
                            <Alert severity="warning" icon={<WarningIcon fontSize="inherit" />} sx={{ '.MuiAlert-message': { flex: 1 } }}>
                              <Typography variant="body2">
                                The due date has been manually changed and no longer matches the SLA.
                              </Typography>
                            </Alert>
                          );
                        }

                        if (slaData && (slaData.permanent_fix_sla_hours || slaData.temp_fix_sla_hours)) {
                          const slaHours = slaData.permanent_fix_sla_hours || slaData.temp_fix_sla_hours;
                          const createdAt = new Date(defectData.rawData.created_at || defectData.rawData.first_detected_at);
                          const slaDueDate = new Date(createdAt.getTime() + slaHours * 60 * 60 * 1000);
                          const isOverdue = slaDueDate < new Date();

                          return (
                            <Alert severity={isOverdue ? "error" : "info"} icon={isOverdue ? <WarningIcon fontSize="inherit" /> : <CheckCircleIcon fontSize="inherit" />} sx={{ '.MuiAlert-message': { flex: 1 } }}>
                              <Typography variant="body2">
                                {isOverdue ? "SLA is overdue. New due date set to 24 hours from now." : "Due date has been automatically set based on the SLA."}
                              </Typography>
                            </Alert>
                          );
                        }

                        return (
                          <Alert severity="info" icon={<CheckCircleIcon fontSize="inherit" />} sx={{ '.MuiAlert-message': { flex: 1 } }}>
                            <Typography variant="body2">
                              No SLA defined. Default due date has been set.
                            </Typography>
                          </Alert>
                        );
                      })()}
                    </Grid>
                  </Grid>
                </Stack>
              </Paper>

              {/* Assignment Section */}
              <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <PersonIcon color="primary" />
                  Assignment Type
                </Typography>

                <RadioGroup
                  value={assignmentType}
                  onChange={handleAssignmentTypeChange}
                  row
                  sx={{ mb: 3 }}
                >
                  <FormControlLabel
                    value="internal"
                    control={<Radio />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonIcon fontSize="small" />
                        <Typography>Internal Team</Typography>
                      </Stack>
                    }
                    sx={{ mr: 4 }}
                  />
                  <FormControlLabel
                    value="external"
                    control={<Radio />}
                    label={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <BusinessIcon fontSize="small" />
                        <Typography>External Contractor</Typography>
                      </Stack>
                    }
                  />
                </RadioGroup>

                {/* Assignment Selection */}
                {loading ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 4,
                    bgcolor: 'grey.50',
                    borderRadius: 2
                  }}>
                    <CircularProgress size={24} sx={{ mr: 2 }} />
                    <Typography>Loading assignment options...</Typography>
                  </Box>
                ) : (
                  <Box>
                    {assignmentType === 'internal' ? (
                      <Stack spacing={2}>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel>Select Internal Users</InputLabel>
                            <Select
                              multiple
                              value={selectedUsers}
                              onChange={(e) => setSelectedUsers(e.target.value)}
                              label="Select Internal Users"
                              open={selectOpen}
                            onOpen={() => setSelectOpen(true)}
                            onClose={() => setSelectOpen(false)}
                            MenuProps={{
                              PaperProps: {
                                sx: { 
                                  maxHeight: 400,
                                  '& .MuiList-root': {
                                    paddingTop: 0
                                  }
                                }
                              }
                            }}
                          >
                            {/* Filter Chips - Sticky Header */}
                            <Box sx={{ 
                              p: 2, 
                              borderBottom: '1px solid', 
                              borderColor: 'divider',
                              position: 'sticky',
                              top: 0,
                              backgroundColor: 'background.paper',
                              zIndex: 1,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Filter by permission scope:
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                                <Chip
                                  label={`Network/Section (${allInternalUsers.filter(u => u.permission_scope === 'network_section').length})`}
                                  size="small"
                                  clickable
                                  color={userFilter === 'network_section' ? 'primary' : 'default'}
                                  variant={userFilter === 'network_section' ? 'filled' : 'outlined'}
                                  onClick={(e) => handleUserFilterChange('network_section', e)}
                                  sx={{ fontSize: '0.75rem' }}
                                />
                                <Chip
                                  label={`Organization (${allInternalUsers.filter(u => u.permission_scope === 'organization').length})`}
                                  size="small"
                                  clickable
                                  color={userFilter === 'organization' ? 'primary' : 'default'}
                                  variant={userFilter === 'organization' ? 'filled' : 'outlined'}
                                  onClick={(e) => handleUserFilterChange('organization', e)}
                                  sx={{ fontSize: '0.75rem' }}
                                />
                                <Chip
                                  label={`All (${allInternalUsers.length})`}
                                  size="small"
                                  clickable
                                  color={userFilter === 'all' ? 'primary' : 'default'}
                                  variant={userFilter === 'all' ? 'filled' : 'outlined'}
                                  onClick={(e) => handleUserFilterChange('all', e)}
                                  sx={{ fontSize: '0.75rem' }}
                                />
                                
                                {/* Search Field - Inline */}
                                <TextField
                                  size="small"
                                  placeholder="Search users..."
                                  value={userSearchTerm}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setUserSearchTerm(e.target.value);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    minWidth: '200px',
                                    flexGrow: 1,
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: 'background.default',
                                      height: '32px'
                                    }
                                  }}
                                />
                              </Stack>
                            </Box>
                            
                            {(Array.isArray(internalUsers) ? internalUsers : []).map((user) => (
                              <MenuItem key={user.id} value={user.id}>
                                <Checkbox checked={selectedUsers.indexOf(user.id) > -1} />
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                    <PersonIcon fontSize="small" />
                                  </Avatar>
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {user.role} • {user.email}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        
                        {(!Array.isArray(internalUsers) || internalUsers.length === 0) && (
                          <Alert severity="info">
                            No internal users available for assignment. Please contact your administrator.
                          </Alert>
                        )}
                      </Stack>
                    ) : (
                      <Stack spacing={3}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Select Contractor Company</InputLabel>
                          <Select
                            value={selectedContractor}
                            onChange={(e) => setSelectedContractor(e.target.value)}
                            label="Select Contractor Company"
                          >
                            {(Array.isArray(matchingContractors) ? matchingContractors : []).map((contractor) => (
                              <MenuItem key={contractor.id || contractor.company_id} value={contractor.id || contractor.company_id}>
                                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                  <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                                    <BusinessIcon fontSize="small" />
                                  </Avatar>
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {contractor.company_name || contractor.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {contractor.location_names?.join(', ') || 'N/A'}
                                    </Typography>
                                  </Box>
                                  <CheckCircleIcon color="success" fontSize="small" />
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {(!Array.isArray(matchingContractors) || matchingContractors.length === 0) && (
                          <Alert severity="warning">
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                              No contractors found for this defect
                            </Typography>
                            <Typography variant="body2">
                              No contractors match both the defect type ({defectData?.defect_type_name}) and location ({defectData?.section_name}) requirements.
                            </Typography>
                          </Alert>
                        )}

                        {/* Selected Contractor Details */}
                        {selectedContractorData && (
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 3,
                              bgcolor: 'success.light',
                              color: 'success.contrastText',
                              borderRadius: 2
                            }}
                          >
                            <Stack spacing={2}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: 'success.main' }}>
                                  <BusinessIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {selectedContractorData.company_name || selectedContractorData.name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Selected Contractor
                                  </Typography>
                                </Box>
                              </Stack>

                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <BuildIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {selectedContractorData.defect_type_names?.length || 0} matching capabilities
                                    </Typography>
                                  </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <LocationIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {selectedContractorData.location_names?.length || 0} matching jurisdictions
                                    </Typography>
                                  </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <PersonIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {selectedContractorData.contact_person}
                                    </Typography>
                                  </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <EmailIcon fontSize="small" />
                                    <Typography variant="body2">
                                      {selectedContractorData.email}
                                    </Typography>
                                  </Stack>
                                </Grid>
                              </Grid>

                              <Box sx={{ 
                                p: 2, 
                                bgcolor: 'rgba(255,255,255,0.1)', 
                                borderRadius: 1 
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                                  Matching Criteria:
                                </Typography>
                                <Typography variant="body2">
                                  • Defect: {selectedContractorData.defect_type_names?.join(', ') || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                  • Location: {selectedContractorData.location_names?.join(', ') || 'N/A'}
                                </Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        )}
                      </Stack>
                    )}
                  </Box>
                )}
              </Paper>
            </Stack>
          </form>
        </Box>
      </DialogContent>

      {/* Sticky Footer */}
      <DialogActions 
        sx={{ 
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 3
        }}
      >
        <Button 
          onClick={handleClose} 
          disabled={submitting}
          size="large"
          variant="outlined"
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained" 
          disabled={submitting || loading}
          size="large"
          sx={{ minWidth: 160 }}
        >
          {submitting ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={20} color="inherit" />
              <Typography>Creating...</Typography>
            </Stack>
          ) : (
            'Create Action Log'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IssueWorkOrderModal; 